import {connect} from 'react-redux';

import {AsyncMoreStatusesEnum} from '../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ScrollDirectionsEnum} from '../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {ResolveStateProps} from '../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CellsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {buildIndexMap} from '../../../srcCommon/helpers/itemIndexHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {AppState} from '../../store/appState';
import {updateSizeAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getSizes} from '../../store/data/localPreferences/localPreferencesSelectors';
import {getUserId} from '../../store/data/teammates/teammatesSelectors';
import {
  FetchCellsCachePoliciesEnum,
  fetchMoreAuditAsyncAction,
  navigateCellsAsyncAction,
} from '../../store/panels/cells/cellsPanelActions';
import {makeGetCellsPanelCurrentPage} from '../../store/panels/cells/cellsPanelSelectors';
import {
  CellsPanelAuditTrailPageState,
  CellsPanelPageTypesEnum,
} from '../../store/panels/cells/cellsPanelState';
import {InboxesAuditTrailParameters} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {AuditTrailCells} from './auditTrailCells';

/*
 * Props.
 */

interface AuditTrailActivityCellsContainerProps {
  panelId: CellsPanelId;
  onParametersChange: (parameters: InboxesAuditTrailParameters) => void;
}

/*
 * Redux.
 */

function getPanelId(_state: AppState, props: AuditTrailActivityCellsContainerProps) {
  return props.panelId;
}

const mapStateToProps = makeMapStateToProps<AuditTrailActivityCellsContainerProps>()(() => {
  const getUntypedPage = makeGetCellsPanelCurrentPage(getPanelId);
  const getPage = (state: AppState, props: AuditTrailActivityCellsContainerProps) => {
    const page = getUntypedPage(state, props);

    // TODO: Find a way to fix this at compile time vs. potentially throwing at runtime.
    if (page.type !== CellsPanelPageTypesEnum.AUDIT_TRAIL) {
      throw new Error('Invalid page type');
    }

    return page;
  };
  const getSortedActivities = (state: AppState, props: AuditTrailActivityCellsContainerProps) => {
    const page = getPage(state, props);
    return page.activities;
  };
  const getSortedActivityIds = createSelector([getSortedActivities], (sortedActivities) =>
    sortedActivities.map((a) => a.id),
  );
  const getActivityIndexes = createSelector([getSortedActivityIds], (sortedActivityIds) =>
    buildIndexMap(sortedActivityIds),
  );

  return {
    page: getPage,
    sortedActivities: getSortedActivities,
    sortedActivityIds: getSortedActivityIds,
    activityIndexes: getActivityIndexes,
    sizes: getSizes,
    userId: getUserId,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<AuditTrailActivityCellsContainerProps>()((props) => ({
  updateSizes: updateSizeAction,
  onRequestMoreAsync: (page: CellsPanelAuditTrailPageState, direction: ScrollDirectionsEnum) => {
    const {panelId} = props;
    const {moreStatusTop, moreStatusBottom, topBoundary, bottomBoundary} = page;

    // Check if we're allowed to load more in that direction.
    const isTop = direction === ScrollDirectionsEnum.TOP;
    const moreStatus = isTop ? moreStatusTop : moreStatusBottom;
    if (moreStatus !== AsyncMoreStatusesEnum.HAS_MORE) {
      throw new Error('Nothing to load.');
    }

    // Find the boundary that we'll use as reference for the next batch.
    const referenceBoundary = isTop ? topBoundary : bottomBoundary;
    if (!referenceBoundary) {
      throw new Error('A reference boundary could not be found.');
    }

    return fetchMoreAuditAsyncAction(
      panelId,
      page.uid,
      page.parameters,
      direction,
      referenceBoundary,
      FetchCellsCachePoliciesEnum.NO_CACHE,
    );
  },
  onRetryClick: (panelId, parameters) => navigateCellsAsyncAction(panelId, parameters),
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: AuditTrailActivityCellsContainerProps,
) {
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onRetryClick: () => dispatchProps.onRetryClick(ownProps.panelId, stateProps.page.parameters),
    onRequestMoreAsync: async (direction: ScrollDirectionsEnum) => {
      await dispatchProps.onRequestMoreAsync(stateProps.page, direction);
    },
  };
}

export const AuditTrailCellsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(AuditTrailCells);
